import { type EmailListApiData } from '@navio/nsdk';
import { filterUnitFor, filterUrlState, useFilterSpecValue, useFilterUnits } from 'noddi-ui-web';
import { EMAIL_PROVIDER_OPTIONS, EMAIL_STATUS_OPTIONS, EMAIL_TEMPLATE_OPTIONS, MIME_TYPE_OPTIONS } from 'noddi-util';
import { LANGUAGE_FILTER_OPTIONS } from '../Language/useLanguageFilter';
import { useServiceDepartmentOptions } from '../ServiceDepartment/useServiceDepartmentFilter';
import { useTireHotelLocationOptions } from '../TireHotel/useTireHotelLocationFilter';

type EmailListFilterInput = Omit<
  NonNullable<EmailListApiData['query']>,
  'pageIndex' | 'pageSize' | 'ordering' | 'search'
>;

const emailFilterUnit = filterUnitFor<EmailListFilterInput>();

export const useEmailTableFilters = () => {
  const serviceDepartments = useServiceDepartmentOptions();
  const serviceDepartmentFilter = emailFilterUnit.multiSelect('serviceDepartmentIds', {
    title: 'Service departments',
    description: 'Filter by specific service departments or locations',
    source: serviceDepartments,
    exclude: true
  });
  const { serviceDepartmentIds } = useFilterSpecValue(serviceDepartmentFilter);
  const tireHotelLocations = useTireHotelLocationOptions({ serviceDepartmentIds });

  return useFilterUnits([
    emailFilterUnit.singleSelect('provider', {
      title: 'Provider',
      description: 'Filter by email service provider',
      options: EMAIL_PROVIDER_OPTIONS,
      exclude: 'one'
    }),
    emailFilterUnit.singleSelect('templateName', {
      title: 'Template',
      description: 'Filter by email template used',
      options: EMAIL_TEMPLATE_OPTIONS,
      exclude: 'one'
    }),
    emailFilterUnit.multiSelect('statusEventStatuses', {
      title: 'Status',
      description: 'Filter by email delivery status',
      options: EMAIL_STATUS_OPTIONS,
      exclude: true
    }),
    emailFilterUnit.boolean('hasAttachments', {
      title: 'Has attachments',
      description: 'Filter emails by whether they include file attachments'
    }),
    serviceDepartmentFilter,
    emailFilterUnit.multiSelect('tireHotelLocationIds', {
      title: 'Tire hotel competitors',
      description: 'Filter by specific tire hotel competitors',
      source: tireHotelLocations,
      exclude: true,
      defaultVisibleOptions: 3
    }),
    emailFilterUnit.multiSelect('language', {
      title: 'Language',
      description: 'Filter by specific language',
      options: LANGUAGE_FILTER_OPTIONS,
      exclude: true
    }),
    emailFilterUnit.singleSelect('mimeType', {
      title: 'MIME type',
      description: 'Filter by email content type',
      options: MIME_TYPE_OPTIONS,
      exclude: 'one'
    }),
    emailDeepLinkFilter
  ]);
};

const FROM_NAME_URL_KEY = filterUrlState.urlKey('fromName');
const FROM_EMAIL_URL_KEY = filterUrlState.urlKey('fromEmail');
const TIRE_HOTEL_EMAIL_IDS_URL_KEY = filterUrlState.urlKey('tireHotelEmailIds');

const keepText = (value: string) => value || undefined;

/** Deep-link params with no control (a sender, a tire hotel's emails): read from the URL, never shown. */
const emailDeepLinkFilter = emailFilterUnit.compose(
  'email-deep-link',
  {
    [FROM_NAME_URL_KEY]: filterUrlState.stored<string>(String, keepText),
    [FROM_EMAIL_URL_KEY]: filterUrlState.stored<string>(String, keepText),
    [TIRE_HOTEL_EMAIL_IDS_URL_KEY]: filterUrlState.excluded(filterUrlState.parseNumber)
  },
  (values) => ({
    controls: [],
    params: {
      fromName: filterUrlState.storedValue(values[FROM_NAME_URL_KEY] as string | null | undefined, undefined),
      fromEmail: filterUrlState.storedValue(values[FROM_EMAIL_URL_KEY] as string | null | undefined, undefined),
      tireHotelEmailIds: values[TIRE_HOTEL_EMAIL_IDS_URL_KEY] as number[]
    }
  })
);
